import React, { useState, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Labeled from 'admin/components/atoms/Labeled';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import Select from 'admin/components/atoms/Select';
import DateTime from 'admin/components/atoms/DateTime';
import { getUserAttributesSchema } from 'admin/actions/customers';
import SchemaRadioOptions from './SchemaRadioOptions';
import SchemaCheckOptions from './SchemaCheckOptions';
import moment from 'moment';

const FormWrapper = styled.details`
  padding: 0.5em;

  ${Labeled} {
    margin: 10px;
    align-items: center;
    display: inline-block;
  }
  ${Select} {
    width: 150px;
  }
`;

// parse string of array to array
// e.g '[1,2,3]' => [1,2,3]
// invalid string returns empty array
const safeParseArray = (str) => {
  if (!str) return [];
  if (typeof str !== 'string') return [];
  if (str.length < 2) return [];
  if (str[0] !== '[' || str[str.length - 1] !== ']') return [];
  return str
    .substring(1, str.length - 1)
    .split(',')
    .map((s) => s.trim());
};

const SchemaFilterForm = ({ onChangeQuery, selectedMoney, isClear, initialQueries = {} }) => {
  const [schemaProps, setSchemaProps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dates, setDates] = useState({});
  const [textRefs, setTextRefs] = useState([]);
  const [prefectures, setPrefectures] = useState({});

  const prefix_id_str = '#/properties/';
  const omitPrefixId = id_str => id_str.substring(prefix_id_str.length);

  const isExistsSchemaProps = schemaProps.length !== 0;
  const privateMoneyId = typeof selectedMoney === 'string' ? selectedMoney : selectedMoney.id;

  useEffect(() => {
    if (isClear && isExistsSchemaProps) {
      textRefs.forEach((textRef) => {
        const ref = textRef;
        if (ref !== null) ref.current.value = '';
      });
      setDates({});
      setPrefectures({});
    }
  }, [isClear]);

  useEffect(() => {
    getUserAttributesSchema(privateMoneyId).then((element) => {
      setOrders(element.property_order);
      setSchemaProps(element.properties);

      const refs = element.property_order.map((order) => {
        const prop = element.properties[order];
        if (prop.form_type === 'text'
            || prop.form_type === 'number'
            || prop.form_type === 'integer'
            || prop.form_type === 'zip_code'
            || prop.form_type === 'email'
            || prop.form_type === 'url'
            || prop.form_type === 'regex') {
          return createRef();
        }
        return null;
      });
      setTextRefs(refs);
    }).catch(() => {
      setOrders([]);
      setSchemaProps([]);
    });
  }, [selectedMoney]);

  const setSchemaDate = (id, value) => {
    if (value) {
      onChangeQuery(id, value.toISOString());
    }
    setDates(oldObj => ({ ...oldObj, [id]: value }));
  };

  const setPrefecturesParam = (id, op) => {
    if (op) {
      onChangeQuery(id, op.value);
    }
    setPrefectures(oldObj => ({ ...oldObj, [id]: op }));
  };

  const makeForm = (prop) => {
    const id = omitPrefixId(prop.id);
    if (prop.form_type === 'text'
        || prop.form_type === 'number'
        || prop.form_type === 'integer'
        || prop.form_type === 'zip_code'
        || prop.form_type === 'email'
        || prop.form_type === 'url'
        || prop.form_type === 'regex') {
      return (
        <LabeledTextBox
          key={`form-${prop.id}`}
          labelText={prop.field_label}
          pattern={prop.regex}
          onBlur={e => onChangeQuery(id, e.target.value)}
          style={{ backgroundColor: 'white' }}
          innerRef={textRefs[orders.indexOf(id)]}
          value={initialQueries[id]}
        />
      );
    } else if (prop.form_type === 'date_select') {
      return (
        <Labeled key={`form-${prop.id}`} labelText={prop.field_label} >
          <DateTime
            id={`form-${prop.id}`}
            value={dates[id] ?? (initialQueries[id] ? moment(initialQueries[id]) : null)}
            onChange={date => setSchemaDate(id, date)}
            timeFormat={null}
          />
        </Labeled>
      );
    } else if (prop.form_type === 'radio') {
      return (
        <div key={`form-${prop.id}`} style={{ display: 'inline' }} >
          <Labeled labelText={prop.field_label} style={{ display: 'inline' }} >
            <SchemaRadioOptions
              id={id}
              options={prop.options}
              onChangeQuery={onChangeQuery}
              isClear={isClear}
              initialOption={initialQueries[id]}
            />
          </Labeled>
        </div>
      );
    } else if (prop.form_type === 'checkbox') {
      return (
        <div key={`form-${prop.id}`} style={{ display: 'inline' }} >
          <Labeled labelText={prop.field_label} style={{ display: 'inline' }} >
            <SchemaCheckOptions
              id={id}
              options={prop.options}
              onChangeQuery={onChangeQuery}
              isClear={isClear}
              initialSelectedOptions={safeParseArray(initialQueries[id])}
            />
          </Labeled>
        </div>
      );
    } else if (prop.form_type === 'prefectures') {
      return (
        <div key={`form-${prop.id}`} style={{ display: 'inline' }} >
          <LabeledSelect
            labelText={prop.field_label}
            options={prop.options}
            value={
              prefectures[id] ??
              (initialQueries[id] ? { value: initialQueries[id], label: initialQueries[id] } : null)
            }
            onChange={v => setPrefecturesParam(id, v)}
            isClear={isClear}
          />
        </div>
      );
    }
    return null;
  };

  const rows = orders.map(key => (
    schemaProps[key] !== undefined ? makeForm(schemaProps[key]) : null
  ));

  return (
    <div>
      {isExistsSchemaProps
       ? (
         <FormWrapper>
           <summary>詳細検索</summary>
           {rows}
         </FormWrapper>
       )
       : null}
    </div>
  );
};

SchemaFilterForm.propTypes = {
  onChangeQuery: PropTypes.func.isRequired,
  selectedMoney: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]).isRequired,
  isClear: PropTypes.bool.isRequired,
};

export default SchemaFilterForm;
